import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import DocxPreviewer from './DocxViewer';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
function range(start, end) {
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}
export const SearchableDocViewer = ({ maxHeight, containerWidth, fileUrl, document, onClose, }) => {
    const pageRefs = useRef({});
    const containerRef = useRef(null);
    const [pageWidth, setPageWidth] = useState(2048);
    const [scale, setScale] = useState(1); // Zoom scale
    // const [pageNumberHighlight, setPageNumberHighlight] = useState(null);
    // const [pdfHighlights, setPdfHighlights] = useState<PdfHighlight[]>([]);
    const [{ numPages, fromPagesRendered, toPagesRendered }, setState] = useState({
        numPages: null,
        fromPagesRendered: null,
        toPagesRendered: null,
    });
    const onRenderSuccess = () => {
        setState((prevState) => ({
            numPages: prevState.numPages,
            fromPagesRendered: prevState.fromPagesRendered,
            toPagesRendered: prevState.toPagesRendered + 1,
        }));
    };
    function onDocumentLoadSuccess({ numPages }) {
        setState({
            numPages,
            fromPagesRendered: 0,
            toPagesRendered: 0,
        });
    }
    const updateScale = () => {
        if (containerRef.current) {
            const viewerWidth = containerWidth || containerRef.current.clientWidth;
            const scaleFactor = viewerWidth / pageWidth;
            setScale(scaleFactor);
        }
    };
    const zoomIn = () => {
        setScale((prevScale) => prevScale + 0.1);
    };
    const zoomOut = () => {
        setScale((prevScale) => prevScale - 0.1);
    };
    // Adjust the scale based on the width of the viewer (initial zoom)
    useEffect(() => {
        updateScale();
        // Update the scale when the window is resized
        window.addEventListener('resize', updateScale);
        // Cleanup the event listener on component unmount
        return () => window.removeEventListener('resize', updateScale);
    }, [pageWidth]);
    const pagesRenderedPlusOne = Math.min(toPagesRendered + 1, numPages);
    return (React.createElement("div", { ref: containerRef, style: { display: 'flex', flexDirection: 'column', flexGrow: 1 } },
        React.createElement("div", { className: "blue", style: { fontWeight: 500 } },
            "File: ",
            document.filename),
        React.createElement("div", { style: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                paddingTop: 20,
                paddingBottom: 10,
                gap: 20,
            } },
            React.createElement("div", { style: { display: 'flex', marginLeft: 'auto', gap: 10 } },
                React.createElement(ZoomOutOutlined, { onClick: zoomOut, disabled: scale <= 0.5 }),
                React.createElement(ZoomInOutlined, { onClick: zoomIn, disabled: scale >= 3 })),
            React.createElement(Tooltip, null,
                React.createElement("div", { className: "clickable", style: { color: 'red', fontWeight: 700 }, onClick: onClose, onKeyDown: () => { }, role: "button", tabIndex: 0 }, "X"))),
        React.createElement("div", { style: {
                maxHeight: maxHeight || '100vh',
                overflow: 'auto',
            } }, document?.s3_key && document?.s3_key.endsWith('.docx') ? (React.createElement(DocxPreviewer, { document: document })) : (
        // <iframe
        //   title="microsoft-doc-viewer"
        //   src={`https://view.officeapps.live.com/op/embed.aspx?src=${'https://relyant-documents-dev.s3.us-west-2.amazonaws.com/cv-reports/77642013-930f-4d20-a1db-38a5b8667a3d.docx?response-content-disposition=inline&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Security-Token=IQoJb3JpZ2luX2VjENj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLXdlc3QtMiJGMEQCIEg8jVkIKlxF74G5nWRPIf1fOqQXF6%2Bsz3DDJ0zknC%2BmAiABfjjSbqSZ9ZY%2F7JFLf1w1wrPNGY1yOsYPe9B4zym1WSrABAjx%2F%2F%2F%2F%2F%2F%2F%2F%2F%2F8BEAAaDDMyNDAzNzI4NzgzMSIMmIgloCWsUO%2BtrbHZKpQEbBtTemzbJPii2B68sU%2Fg%2B%2FwbVYmPpgUcKvRz8a1SRwqjpv%2FyYGOO2dNAy4LQQSl9m8LPUXQ9KYykPjTS%2FGpFZVDqxNXd0F5T9cNmqVs8gxiEFUOEXXXazRpp%2F3R%2BMQhDABVwhjnmvKGY03XUJbtsVuHO9JroMpVWbI6Y%2FJ%2FWb5dWBnwnqd4ggtQ3tKR5MrcNFUlDpG9ptPUpKHRZ%2FGTxAqzi6UhtkRP9RvZF7eeyq2E50Y33v1WuO3SHcFV7bTWhKuMTQHabyX4hSPBdrZvxOjbX9VGhcYmfAhEQc0Y%2B%2F0Kl5Y3Po0Zb9HWeEzC2Z3vBzG%2BDs4Yn5ghVUxMx6LHa1ukntR4h2BMu1d4lERH%2FvTZUqNV19DcOU7SM%2BXisE0SDq4WMA6Wqr4jsIii6JdGeJ9xtgeYAypPhes3tnKU2FtmHia64SWRJaHhx5K%2FfTyrdGG4t9GF%2BOsBJv2Fk8H%2BljtzMTJ9zXAHnlH6mfylHpGJVS4HfJGe%2FuW%2BgFBWDErJzolr69KMcdltKD%2FyoqqZITU36L%2BLfr%2F%2B2QndvqWqp%2FRmUVxptzBW20K15aLtHyZyAK5c8M4fcTPkvHFVICRHu7534I%2B1XCVPbQon0NiD0iQpiScK2vf71N2cVPy%2FirWSown5TzBMyeFImwvmAZANHblcfzEMC9dHM0qY0p9FDtyMv%2FLObHxOADtKlkzi02DXY0LcHsTD%2BgrK9BjrGAmGLcfK2wH7hYk23p8mZdYgL2BBQcKuMZlYtOJJz4YRZ9QE2mF8FLdlwQhN1phQQjcTVxJebgpBYHUlRb44VIHIyy6d4Xrf%2BtC9DOVbgk34C5rCr47xphzqEZxgUrpEX%2FzgrfB69TscXLpVQFCxxq%2FYtXVJSNan12%2B2A%2FFPQpqMTl6B38K291ZrSwQ9hU3pHsUmQNg0vRXL3696fuXjUKmhNB2M16lOVWhhaGRvSWtiTR7Pjz9K7BdyOaCkzcxce7Z75tYXUzwORnfswJVbFzJe5Neutjpucz1AImzCy25u6i%2BEcEsbW15CLsahpiurj6cXPdqxDfyZgyLW%2FPySMdRvBCOqKFkbEmcZeh86ODOV8iGn3j9iI2olOdZ5DGFyr2QlKjHl6szOTdqLoBCuzPcwouqNFJ8PSAZ%2BQfAw7hqytRD9%2B1WHa&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIAUW4RAT6LYFVL4RH4%2F20250212%2Fus-west-2%2Fs3%2Faws4_request&X-Amz-Date=20250212T160857Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host&X-Amz-Signature=34332e21ccf1c085012ac76b10df5fdfb323798277e463f6282e25c3d7d0b942'}`}
        //   style={{ width: '100%', height: '600px', border: 'none' }}
        // />
        // <iframe
        //   title="google-doc-viewer"
        //   src={`https://docs.google.com/gview?url=${document?.url}&embedded=true`}
        //   style={{ width: '100%', height: '600px', border: 'none' }}
        // />
        React.createElement(Document, { file: document?.url || fileUrl, onLoadSuccess: onDocumentLoadSuccess, loading: '' }, range(fromPagesRendered, toPagesRendered).map((el, index) => {
            const pNum = el + 1;
            const isCurrentlyRendering = pagesRenderedPlusOne === pNum;
            const isLastPage = numPages === pNum;
            const needsCallbackToRenderNextPage = isCurrentlyRendering && !isLastPage;
            return (React.createElement("div", { ref: (el) => {
                    pageRefs.current[pNum] = el;
                }, key: `page_${pNum}`, style: { position: 'relative', marginBottom: '20px' } },
                React.createElement(Page, { id: `${pNum}`, pageNumber: pNum, loading: '', onRenderSuccess: needsCallbackToRenderNextPage ? onRenderSuccess : null, scale: scale, width: pageWidth })));
        }))))));
};
